import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClose,
  faCheckCircle,
  faCancel,
  faReceipt,
  faTrash,
  faSpinner,
  faCheck,
  faCashRegister,
  faPlus,
  faBarcode,
  faTag,
} from "@fortawesome/free-solid-svg-icons";
import useSWR, { mutate } from "swr";
import DashboardContenLeftMenus from "./DashboardContenLeftMenus";
import DashboardContentHeader from "./DashboardContentHeader";
import DashboardContentFooter from "./DashboardContentFooter";
import TopActionMenus from "./TopActionMenus";
import MessageModal from "./MessageModal";
import SecureLS from "secure-ls";
import { useNavigationEvent } from "navigation-react";

const ls = new SecureLS({ encodingType: "aes" });

const username = "admin1";
const password = "abc@123@Q";
const authHeader = "Basic " + btoa(`${username}:${password}`);

// Function to get CSRF token
const getCsrfToken = async () => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_BASE_URL}/session/token`,
      {
        credentials: "include",
        headers: {
          Authorization: authHeader,
          Accept: "text/plain",
        },
      },
    );

    if (response.ok) {
      const token = await response.text();
      ls.set("usercsrf_token", token);
      return token;
    }

    const storedToken = ls.get("usercsrf_token");
    if (storedToken) {
      return storedToken;
    }

    return null;
  } catch (error) {
    console.error("Error getting CSRF token:", error);
    return ls.get("usercsrf_token") || null;
  }
};

const fetcher = async (url) => {
  try {
    const csrfToken = await getCsrfToken();

    const headers = {
      Authorization: authHeader,
      Accept: "application/vnd.api+json",
      "Content-Type": "application/vnd.api+json",
    };

    if (csrfToken) {
      headers["X-CSRF-Token"] = csrfToken;
    }

    const response = await fetch(url, {
      credentials: "include",
      headers: headers,
    });

    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }

    return response.json();
  } catch (error) {
    console.error("Fetch error:", error);
    throw error;
  }
};

/* ==============================
   PAYMENT OPTIONS
============================== */
const paymentOptions = [
  { value: "cash", label: "Cash" },
  { value: "credit", label: "Credit" },
  { value: "mobile_money", label: "Mobile Money" },
  { value: "card", label: "Card" },
  { value: "bank_transfer", label: "Bank Transfer" },
];

export default function SalesOrder() {
  const { stateNavigator } = useNavigationEvent();
  const cashierUid = ls.get("currentuserid");

  /* ==============================
     STATE
  ============================== */
  const [loading, setLoading] = useState(false);
  const [customer, setCustomer] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [orderNumber, setOrderNumber] = useState("");
  const [paymentType, setPaymentType] = useState("cash");
  const [amountPaid, setAmountPaid] = useState("");
  const [discount, setDiscount] = useState(""); // Fixed amount discount in GHC
  const [paymentReference, setPaymentReference] = useState("");
  const [items, setItems] = useState([]);
  const [notes, setNotes] = useState("");
  const [barcode, setBarcode] = useState("");
  const [productSearchTerm, setProductSearchTerm] = useState("");
  const [modalSuccessMsg, setModalSuccessMsg] = useState(false);
  const [modalError, setModalError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const [newCustomerForm, setNewCustomerForm] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
  });

  const barcodeInputRef = useRef();
  const customerInputRef = useRef();

  /* ==============================
     CALCULATIONS
  ============================== */
  const subtotal = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
  const discountAmount = parseFloat(discount) || 0; // Fixed amount in GHC
  const totalAmount = subtotal - discountAmount; // Net amount after discount
  const paidAmount = parseFloat(amountPaid) || 0;
  const remainingBalance = totalAmount - paidAmount;

  /* ==============================
     DATA
  ============================== */
  const { data: customers } = useSWR(
    `${process.env.REACT_APP_CUSTOMER_API}?fields[node--customer]=title,field_phone_number&page[limit]=1000`,
    fetcher,
  );

  const { data: productList } = useSWR(
    `${process.env.REACT_APP_ADD_PRODUCT_API}?fields[node--product]=title,field_price,field_sku,field_gtin,field_stock_quantity&page[limit]=1000`,
    fetcher,
  );

  /* ==============================
     FILTERS
  ============================== */
  const filteredCustomers =
    customers?.data?.filter(
      (c) =>
        c.attributes.title
          .toLowerCase()
          .includes(customerSearchTerm.toLowerCase()) ||
        c.attributes.field_phone_number
          ?.toLowerCase()
          .includes(customerSearchTerm.toLowerCase()),
    ) || [];

  const filteredProducts =
    productList?.data?.filter(
      (p) =>
        p.attributes.title
          .toLowerCase()
          .includes(productSearchTerm.toLowerCase()) ||
        p.attributes.field_sku
          ?.toLowerCase()
          .includes(productSearchTerm.toLowerCase()),
    ) || [];

  /* ==============================
     EFFECTS
  ============================== */
  useEffect(() => {
    customerInputRef.current?.focus();
    setOrderNumber(`PS-${Date.now()}-${Math.floor(Math.random() * 1000)}`);
  }, []);

  /* ==============================
     HELPERS
  ============================== */
  const isOutOfStock = (product) =>
    Number(product?.attributes?.field_stock_quantity || 0) <= 0;

  /* ==============================
     CART
  ============================== */
  const handleAddProductToCart = (product) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.product_uuid === product.id);
      if (existing) {
        return prev.map((i) =>
          i.product_uuid === product.id
            ? { ...i, quantity: i.quantity + 1 }
            : i,
        );
      }
      return [
        ...prev,
        {
          product_nid: product.id,
          product_uuid: product.id,
          title: product.attributes.title,
          sku: product.attributes.field_sku,
          price: Number(product.attributes.field_price),
          quantity: 1,
        },
      ];
    });
  };

  const updateQuantity = (product_uuid, qty) => {
    const quantity = parseInt(qty, 10);
    if (isNaN(quantity) || quantity < 1) return;

    setItems((prev) =>
      prev.map((i) =>
        i.product_uuid === product_uuid ? { ...i, quantity } : i,
      ),
    );
  };

  const removeItem = (product_uuid) => {
    setItems((prev) => prev.filter((i) => i.product_uuid !== product_uuid));
  };

  /* ==============================
     BARCODE
  ============================== */
  const handleBarcodeScan = (code) => {
    const product = productList?.data?.find(
      (p) =>
        p.attributes.field_gtin === code || p.attributes.field_sku === code,
    );

    if (!product) {
      setErrorMessage(`Product "${code}" not found`);
      setModalError(true);
      return;
    }

    if (isOutOfStock(product)) {
      setErrorMessage(`Insufficient stock for ${product.attributes.title}`);
      setModalError(true);
      return;
    }

    handleAddProductToCart(product);
    setBarcode("");
    barcodeInputRef.current?.focus();
  };

  /* ==============================
     ADD CUSTOMER
  ============================== */
  const handleAddNewCustomer = async (e) => {
    e.preventDefault();
    setLoading(true);

    let res;
    try {
      const csrfToken = await getCsrfToken();
      res = await fetch(process.env.REACT_APP_CUSTOMER_API, {
        method: "POST",
        credentials: "include",
        headers: {
          Authorization: authHeader,
          "Content-Type": "application/vnd.api+json",
          Accept: "application/vnd.api+json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({
          data: {
            type: "node--customer",
            attributes: {
              title: newCustomerForm.name || "Unknown Customer",
              field_phone_number: newCustomerForm.phone || "",
              field_customer_email: newCustomerForm.email || "",
              field_customer_address: newCustomerForm.address || "",
            },
          },
        }),
      });
    } catch (e) {
      setErrorMessage(e.message);
      setModalError(true);
      setLoading(false);
      return;
    }

    const json = await res.json();
    if (json.errors && json.errors[0]) {
      alert(`Customer Error: ${json.errors[0].detail}`);
    }

    setCustomer(json.data.id);
    setCustomerName(json.data.attributes.title);
    setShowCustomerModal(false);
    setNewCustomerForm({ name: "", phone: "", email: "", address: "" });

    mutate(
      `${process.env.REACT_APP_CUSTOMER_API}?fields[node--customer]=title,field_phone_number`,
    );
    setLoading(false);
  };

  /* ==============================
     CHECKOUT WITH SINGLE PAYMENT
  ============================== */
  const handleAddSales = async () => {
    if (!customer || !items.length) {
      alert("Please select a customer and add at least one item.");
      return;
    }

    // VALIDATION - Payment amount cannot be empty
    if (!amountPaid || amountPaid === "" || parseFloat(amountPaid) < 0) {
      alert("Please enter a valid payment amount");
      return;
    }

    // Validate discount
    if (discountAmount < 0) {
      alert("Discount cannot be negative");
      return;
    }

    if (discountAmount > subtotal) {
      alert("Discount cannot exceed subtotal");
      return;
    }

    if (paidAmount > totalAmount) {
      alert("Amount paid cannot exceed total amount");
      return;
    }

    // WARNING: If partial payment, confirm with user
    if (remainingBalance > 0) {
      const confirmPartial = window.confirm(
        `This is a partial payment. Balance due: GHC${remainingBalance.toFixed(
          2,
        )}\n` + `Continue?`,
      );
      if (!confirmPartial) {
        return;
      }
    }

    setLoading(true);
    try {
      const csrfToken = await getCsrfToken();

      const res = await fetch(
        `${process.env.REACT_APP_BASE_URL}/pos/checkout`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify({
            order_number: orderNumber,
            customer_nid: customer,
            cashier_uid: cashierUid,
            payment_type: paymentType,
            amount_paid: paidAmount,
            discount: discountAmount, // Send fixed amount discount
            payment_reference: paymentReference,
            notes: notes,
            items: items.map((i) => ({
              product_uuid: i.product_uuid,
              qty: Number(i.quantity),
              unit_price: Number(i.price),
            })),
          }),
        },
      );

      const json = await res.json();
      if (!res.ok) {
        setErrorMessage(json.message || "Failed to create order");
        setModalError(true);
        setLoading(false);
        return;
      }

      // Set success message based on payment status
      let message = "Order created successfully! ";
      if (discountAmount > 0) {
        message += `(Discount Applied: GHC${discountAmount.toFixed(2)}) `;
      }
      if (remainingBalance === 0) {
        message += "✅ Fully Paid";
      } else if (remainingBalance > 0) {
        message += `⚠️ Partial Payment - Balance Due: GHC${remainingBalance.toFixed(
          2,
        )}`;
      }

      setSuccessMessage(message);
      setModalSuccessMsg(true);

      // Auto-reset form after 3 seconds
      setTimeout(() => {
        resetForm();
      }, 3000);
    } catch (e) {
      setErrorMessage(e.message || "Network error");
      setModalError(true);
    }
    setLoading(false);
  };

  /* ==============================
     RESET FORM
  ============================== */
  const resetForm = () => {
    setCustomer("");
    setCustomerName("");
    setItems([]);
    setNotes("");
    setBarcode("");
    setCustomerSearchTerm("");
    setProductSearchTerm("");
    setPaymentType("cash");
    setAmountPaid("");
    setDiscount(""); // Reset discount
    setPaymentReference("");
    setSuccessMessage("");
    setModalSuccessMsg(false);
    setOrderNumber(`PS-${Date.now()}-${Math.floor(Math.random() * 1000)}`);
  };

  /* ==============================
     UI
  ============================== */
  return (
    <div className="dashboard-container">
      <DashboardContentHeader />
      <div className="content-wrapper">
        <DashboardContenLeftMenus />
        <div className="sales-container" style={styles.container}>
          <TopActionMenus
            AddTitle="Add Sales"
            buttonLabel="Sales Register"
            icon={faReceipt}
            onClickIcon={faCashRegister}
            onClick={() => stateNavigator.navigate("viewsalesregister")}
          />

          <div style={styles.gridwrapper}>
            {/* LEFT COLUMN */}
            <div style={styles.productcard}>
              <div style={styles.grid}>
                {/* Order Number */}
                <div>
                  <label style={styles.label}>Invoice Number</label>
                  <input
                    type="text"
                    style={styles.input}
                    value={orderNumber}
                    readOnly
                  />
                </div>

                {/* Payment Type */}
                <div>
                  <label style={styles.label}>Payment Type *</label>
                  <select
                    style={styles.input}
                    value={paymentType}
                    onChange={(e) => setPaymentType(e.target.value)}
                    required
                  >
                    {paymentOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Customer Search */}
              <div style={{ marginTop: "20px" }}>
                <label style={styles.label}>Customer *</label>
                <div
                  style={{ display: "flex", gap: "10px", position: "relative" }}
                >
                  <input
                    type="text"
                    ref={customerInputRef}
                    style={styles.input}
                    placeholder="Search customer..."
                    value={customerName || customerSearchTerm}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (customerName && value !== customerName) {
                        setCustomer("");
                        setCustomerName("");
                      }
                      setCustomerSearchTerm(value);
                    }}
                    required
                  />
                  <button
                    style={styles.button}
                    onClick={() => setShowCustomerModal(true)}
                    type="button"
                  >
                    <FontAwesomeIcon icon={faPlus} />
                  </button>
                  {!customerName &&
                    customerSearchTerm &&
                    filteredCustomers.length > 0 && (
                      <div style={styles.searchResults}>
                        {filteredCustomers.length > 0 ? (
                          filteredCustomers.map((cust) => (
                            <div
                              key={cust.id}
                              style={styles.searchItem}
                              onClick={() => {
                                setCustomer(cust.id);
                                setCustomerName(cust.attributes.title);
                                setCustomerSearchTerm("");
                              }}
                              onMouseEnter={(e) =>
                                (e.currentTarget.style.background = "#f8f9fa")
                              }
                              onMouseLeave={(e) =>
                                (e.currentTarget.style.background = "#fff")
                              }
                            >
                              <h2
                                style={{
                                  fontSize: "13px",
                                  color: "#333",
                                  marginBottom: "4px",
                                }}
                              >
                                {cust.attributes.title}
                              </h2>
                              <div style={{ fontSize: "13px", color: "#333" }}>
                                {cust.attributes.field_phone_number ||
                                  "No phone number"}
                              </div>
                            </div>
                          ))
                        ) : (
                          <div style={styles.noResult}>No customers found</div>
                        )}
                      </div>
                    )}
                </div>
              </div>

              {/* Barcode Scanner */}
              <div style={{ marginTop: "20px" }}>
                <label style={styles.label}>Barcode Scanner</label>
                <div style={{ display: "flex", gap: "10px" }}>
                  <input
                    type="text"
                    ref={barcodeInputRef}
                    style={styles.input}
                    placeholder="Scan barcode or enter manually"
                    value={barcode}
                    onChange={(e) => setBarcode(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && barcode.trim()) {
                        handleBarcodeScan(barcode.trim());
                      }
                    }}
                  />
                  <button
                    style={styles.button}
                    onClick={() => {
                      if (barcode.trim()) {
                        handleBarcodeScan(barcode.trim());
                      }
                    }}
                  >
                    <FontAwesomeIcon icon={faBarcode} />
                  </button>
                </div>
              </div>

              {/* Product Search */}
              <div
                style={{
                  backgroundColor: "#acb0b6ff",
                  marginTop: "15px",
                  padding: "12px",
                  borderRadius: "10px",
                }}
              >
                <label style={styles.label}>Search Products</label>
                <div style={{ position: "relative" }}>
                  <input
                    type="text"
                    style={styles.input}
                    placeholder="Search products by name..."
                    value={productSearchTerm}
                    onSelect={() => {
                      mutate(
                        `${process.env.REACT_APP_ADD_PRODUCT_API}?fields[node--product]=title,field_sku,field_price,field_gtin,field_stock_quantity,field_product_image,drupal_internal__nid,uuid&include=field_product_image`,
                      );
                    }}
                    onChange={(e) => setProductSearchTerm(e.target.value)}
                  />
                  {productSearchTerm && (
                    <div style={styles.searchResults}>
                      {filteredProducts.length > 0 ? (
                        filteredProducts.map((product) => (
                          <div
                            key={product.id}
                            style={styles.searchItem}
                            onClick={() => {
                              if (isOutOfStock(product)) {
                                setErrorMessage(
                                  `Insufficient stock for ${product.attributes.title}`,
                                );
                                setModalError(true);
                                return;
                              }
                              handleAddProductToCart(product);
                            }}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.background = "#f8f9fa")
                            }
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.background = "#fff")
                            }
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                              }}
                            >
                              <div>
                                <h2
                                  style={{
                                    fontSize: "13px",
                                    color: "#333",
                                    marginBottom: "4px",
                                  }}
                                >
                                  {product.attributes.title}
                                </h2>
                                <div
                                  style={{ fontSize: "13px", color: "#333" }}
                                >
                                  SKU: {product.attributes.field_sku} | Price:
                                  GHC
                                  {Number(
                                    product.attributes.field_price || 0,
                                  ).toFixed(2)}{" "}
                                  | Stock:{" "}
                                  {product.attributes.field_stock_quantity || 0}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div style={styles.noResult}>No products found</div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* AMOUNT PAID - REQUIRED */}
              <div
                style={{
                  backgroundColor: "#3d69a2",
                  marginTop: "15px",
                  padding: "12px",
                  borderRadius: "10px",
                }}
              >
                <label style={styles.labelamountpaid}>Amount Paid(GHC) *</label>
                <input
                  type="number"
                  style={styles.input}
                  value={amountPaid}
                  onChange={(e) => setAmountPaid(e.target.value)}
                  placeholder="Enter amount received"
                  step="0.01"
                  min="0"
                  max={totalAmount}
                  required
                />
              </div>

              {/* DISCOUNT FIELD - FIXED AMOUNT (GHC) */}
              <div
                style={{
                  backgroundColor: "#ff9800",
                  marginTop: "15px",
                  padding: "12px",
                  borderRadius: "10px",
                }}
              >
                <label style={styles.labelamountpaid}>
                  Discount Amount (GHC)
                </label>
                <input
                  type="number"
                  style={styles.input}
                  value={discount}
                  onChange={(e) => setDiscount(e.target.value)}
                  placeholder="Enter discount amount in GHC"
                  step="0.01"
                  min="0"
                  max={subtotal}
                />
              </div>

              {/* Payment Reference - ONLY IF NEEDED */}
              {paymentType !== "cash" && (
                <div style={{ marginTop: "15px" }}>
                  <label style={styles.label}>Transaction Reference *</label>
                  <input
                    type="text"
                    style={styles.input}
                    value={paymentReference}
                    onChange={(e) => setPaymentReference(e.target.value)}
                    placeholder="e.g., MOMO123456"
                    required={paymentType !== "cash"}
                  />
                </div>
              )}

              {/* PAYMENT SUMMARY */}
              <div style={styles.paymentSummary}>
                <div style={styles.paymentRow}>
                  <span>Subtotal:</span>
                  <span style={styles.paymentAmount}>
                    GHC{subtotal.toFixed(2)}
                  </span>
                </div>

                {discountAmount > 0 && (
                  <>
                    <div style={styles.paymentRow}>
                      <span style={{ color: "#ff9800" }}>Discount:</span>
                      <span style={{ color: "#ff9800", fontWeight: "bold" }}>
                        - GHC{discountAmount.toFixed(2)}
                      </span>
                    </div>
                    <div
                      style={{
                        ...styles.paymentRow,
                        fontSize: "12px",
                        marginTop: "-5px",
                        marginBottom: "10px",
                      }}
                    >
                      {/* <span style={{ color: "#666" }}>
                        (Equivalent to{" "}
                        {((discountAmount / subtotal) * 100).toFixed(1)}% off)
                      </span> */}
                    </div>
                  </>
                )}

                <div
                  style={{
                    ...styles.paymentRow,
                    borderTop: "1px solid #ddd",
                    paddingTop: "10px",
                    marginTop: "10px",
                  }}
                >
                  <span style={{ fontWeight: "bold", fontSize: "15px" }}>
                    Total Amount:
                  </span>
                  <span
                    style={{
                      fontWeight: "bold",
                      color: "#1976d2",
                      fontSize: "16px",
                    }}
                  >
                    GHC{totalAmount.toFixed(2)}
                  </span>
                </div>

                <div style={styles.paymentRow}>
                  <span>Amount Paid:</span>
                  <span
                    style={
                      paidAmount > 0 ? styles.paidAmount : styles.pendingAmount
                    }
                  >
                    GHC{paidAmount.toFixed(2)}
                  </span>
                </div>

                <div
                  style={{
                    ...styles.paymentRow,
                    borderTop: "1px solid #ddd",
                    paddingTop: "10px",
                    marginTop: "10px",
                  }}
                >
                  <span style={{ fontWeight: "bold" }}>Balance:</span>
                  <span
                    style={
                      remainingBalance > 0
                        ? styles.balanceDue
                        : styles.balancePaid
                    }
                  >
                    GHC{remainingBalance.toFixed(2)}
                  </span>
                </div>
              </div>

              {/* IMPORTANT NOTE
              {remainingBalance > 0 && (
                <div style={styles.warningNote}>
                  <strong>⚠️ Note:</strong> This will be a partial payment
                  order. No additional payments can be added later.
                </div>
              )} */}
            </div>

            {/* RIGHT COLUMN - Cart */}
            <div style={styles.card}>
              {/* Items Table */}
              <div style={styles.tableContainer}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>SKU</th>
                      <th>Price</th>
                      <th>Quantity</th>
                      <th>Total</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => (
                      <tr key={item.product_uuid}>
                        <td>{item.title}</td>
                        <td>{item.sku}</td>
                        <td>GHC{item.price.toFixed(2)}</td>
                        <td>
                          <input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) =>
                              updateQuantity(item.product_uuid, e.target.value)
                            }
                            onBlur={(e) => {
                              if (
                                e.target.value === "" ||
                                parseInt(e.target.value) < 1
                              ) {
                                updateQuantity(item.product_uuid, 1);
                              }
                            }}
                            style={styles.quantityInput}
                          />
                        </td>
                        <td>GHC{(item.price * item.quantity).toFixed(2)}</td>
                        <td>
                          <button
                            style={styles.removeButton}
                            onClick={() => removeItem(item.product_uuid)}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Customer Modal */}
              {showCustomerModal && (
                <div className="modal">
                  <div className="overlay"></div>
                  <div className="modal-content">
                    <div className="modal-body">
                      <div className="modal-header">
                        <p>Add New Customer</p>
                        <button
                          className="close-modal"
                          onClick={() => setShowCustomerModal(false)}
                        >
                          <FontAwesomeIcon icon={faClose} />
                        </button>
                      </div>

                      <form
                        onSubmit={handleAddNewCustomer}
                        className="edit-patient-form"
                      >
                        <div style={styles.formgroupwrapper}>
                          <div className="form-group">
                            <label htmlFor="customer-name">Full Name *</label>
                            <input
                              type="text"
                              placeholder="Enter Customer Full Name"
                              value={newCustomerForm.name}
                              onChange={(e) =>
                                setNewCustomerForm({
                                  ...newCustomerForm,
                                  name: e.target.value,
                                })
                              }
                              required
                            />
                          </div>

                          <div className="form-group">
                            <label htmlFor="customer-contact">
                              Contact Number
                            </label>
                            <input
                              type="text"
                              placeholder="Enter Customer Contact Number"
                              value={newCustomerForm.phone}
                              onChange={(e) =>
                                setNewCustomerForm({
                                  ...newCustomerForm,
                                  phone: e.target.value,
                                })
                              }
                            />
                          </div>
                        </div>

                        <div className="form-group">
                          <label htmlFor="customer-emailaddress">
                            Email Address
                          </label>
                          <input
                            type="email"
                            placeholder="Enter Customer Email Address"
                            value={newCustomerForm.email}
                            onChange={(e) =>
                              setNewCustomerForm({
                                ...newCustomerForm,
                                email: e.target.value,
                              })
                            }
                          />
                        </div>

                        <div className="form-group">
                          <label htmlFor="customer-address">
                            Location Address
                          </label>
                          <input
                            type="text"
                            placeholder="Enter Customer Address"
                            value={newCustomerForm.address}
                            onChange={(e) =>
                              setNewCustomerForm({
                                ...newCustomerForm,
                                address: e.target.value,
                              })
                            }
                          />
                        </div>

                        <div className="submitGroup">
                          <button
                            type="submit"
                            disabled={loading}
                            style={styles.button}
                          >
                            {loading ? (
                              <FontAwesomeIcon
                                icon={faSpinner}
                                spin={true}
                                strokeWidth={20}
                                fontSize={22}
                              />
                            ) : (
                              "Add Customer"
                            )}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              )}

              {/* Notes */}
              <div>
                <label style={styles.label}>Notes</label>
                <textarea
                  style={styles.textarea}
                  placeholder="Add any notes about this order..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>

              {/* Action Buttons */}
              <div style={styles.actionButtons}>
                <button style={styles.secondaryButton} onClick={resetForm}>
                  <FontAwesomeIcon icon={faClose} /> Cancel
                </button>
                <button
                  style={styles.button}
                  onClick={handleAddSales}
                  disabled={loading || items.length === 0 || !customer}
                >
                  {loading ? (
                    <FontAwesomeIcon
                      icon={faSpinner}
                      spin={true}
                      strokeWidth={20}
                      fontSize={22}
                    />
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faCheck} />
                      Create Order & Pay
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <DashboardContentFooter />

      {/* Success Modal */}
      {modalSuccessMsg && (
        <MessageModal
          leftIcon={faCheckCircle}
          color={remainingBalance === 0 ? "green" : "orange"}
          message={successMessage}
          onClose={() => {
            setModalSuccessMsg(false);
            resetForm();
          }}
          rightIcon={faClose}
        />
      )}

      {modalError && (
        <MessageModal
          leftIcon={faCancel}
          color="red"
          message={errorMessage}
          onClose={() => setModalError(false)}
          rightIcon={faClose}
        />
      )}
    </div>
  );
}

/* ==============================
   STYLES
============================== */
const styles = {
  container: {
    width: "100%",
    height: "100%",
    backgroundColor: "#ecf2fa",
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
    padding: "34px 32px 4rem 32px",
  },
  card: {
    background: "#fff",
    padding: "25px",
    borderRadius: "12px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    width: "64%",
    marginTop: "20px",
  },
  productcard: {
    background: "#fff",
    padding: "25px",
    borderRadius: "12px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    width: "36%",
    marginRight: "20px",
    marginTop: "20px",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "20px",
    marginBottom: "20px",
  },
  input: {
    width: "100%",
    padding: "14px 12px",
    borderRadius: "8px",
    border: "1px solid #e0e0e0",
    fontSize: "14px",
    transition: "border 0.3s",
  },
  tableContainer: {
    overflowX: "auto",
    marginTop: "20px",
    borderRadius: "8px",
    border: "1px solid #e0e0e0",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: "800px",
  },
  quantityInput: {
    width: "70px",
    padding: "8px",
    borderRadius: "4px",
    border: "1px solid #ddd",
    textAlign: "center",
  },
  textarea: {
    width: "100%",
    height: "80px",
    borderRadius: "8px",
    border: "1px solid #e0e0e0",
    padding: "12px",
    marginTop: "15px",
    fontSize: "14px",
    resize: "vertical",
  },
  button: {
    background: "#333",
    color: "white",
    padding: "12px 24px",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    cursor: "pointer",
    transition: "all 0.3s",
  },
  secondaryButton: {
    background: "#6c757d",
    color: "white",
    padding: "12px 24px",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    cursor: "pointer",
    transition: "all 0.3s",
  },
  actionButtons: {
    display: "flex",
    gap: "15px",
    marginTop: "20px",
    justifyContent: "flex-end",
  },
  searchResults: {
    background: "#fff",
    border: "1px solid #ddd",
    borderRadius: "8px",
    maxHeight: "250px",
    overflowY: "auto",
    marginTop: "5px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    zIndex: 1000,
    position: "absolute",
    width: "100%",
    top: "100%",
    left: 0,
  },
  searchItem: {
    padding: "12px",
    cursor: "pointer",
    borderBottom: "1px solid #f5f5f5",
    transition: "background 0.2s",
  },
  noResult: {
    padding: "15px",
    color: "#888",
    fontStyle: "italic",
    textAlign: "center",
  },
  removeButton: {
    background: "#dc3545",
    color: "white",
    border: "none",
    borderRadius: "4px",
    padding: "6px 10px",
    cursor: "pointer",
  },
  label: {
    fontWeight: "600",
    marginBottom: "6px",
    display: "block",
    color: "#333",
  },
  labelamountpaid: {
    fontWeight: "600",
    marginBottom: "6px",
    display: "block",
    color: "#fff",
  },
  gridwrapper: {
    display: "flex",
    width: "100%",
  },
  formgroupwrapper: {
    display: "flex",
    gap: "10px",
  },
  paymentSummary: {
    marginTop: "20px",
    marginBottom: "20px",
    padding: "16px",
    backgroundColor: "#f8f9fa",
    borderRadius: "8px",
    border: "1px solid #e0e0e0",
  },
  paymentRow: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "8px",
    fontSize: "14px",
  },
  paymentAmount: {
    fontWeight: "bold",
    color: "#333",
  },
  paidAmount: {
    color: "#28a745",
    fontWeight: "bold",
  },
  pendingAmount: {
    color: "#ffc107",
    fontWeight: "bold",
  },
  balanceDue: {
    color: "#dc3545",
    fontWeight: "bold",
  },
  balancePaid: {
    color: "#28a745",
    fontWeight: "bold",
  },
  warningNote: {
    marginTop: "15px",
    padding: "10px",
    backgroundColor: "#fff3cd",
    border: "1px solid #ffc107",
    borderRadius: "8px",
    fontSize: "13px",
    color: "#856404",
  },
  discountHint: {
    marginTop: "5px",
    textAlign: "left",
  },
};
